import {
  companyDocumentExpiry,
  formatExpiryDate,
  type ExpiryNotice,
} from "@roof-hawk/core";
import { useRef, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  deleteCompanyDocument,
  fetchCompanyDocuments,
  signCompanyDocuments,
  updateRow,
  uploadCompanyDocument,
  type CompanyDocument,
} from "../../lib/queries";
import { useSession } from "../../session";

/**
 * The documents the company keeps sending.
 *
 * Jonathon, 2026-08-15: "company docs is where we'd want commonly used pdfs."
 * The licence, the certificate of insurance, the W-9, the Central States price
 * book, the colour charts. They are not a job's paperwork — they are the same
 * eight or ten files, sent again and again, and today they live in somebody's
 * inbox and get found by scrolling.
 *
 * Three things this screen has that a plain file list does not:
 *
 *   - **A name a person recognises.** The licence arrives called
 *     `31013_260808_220807.pdf`. Nobody picks that off a list.
 *   - **An expiry.** A licence and a COI stop being true on a printed date.
 *     The row says so, loudly once it is close, because attaching an expired
 *     licence to a bid is worse than attaching nothing.
 *   - **Retire rather than delete.** A superseded document leaves the picker
 *     and stays attached to everything already sent. Deleting is for something
 *     uploaded by mistake, and only Lysa, Sam and Jonathon may do it —
 *     decided 2026-08-02, and enforced by the database, not by this button.
 */
export function CompanyDocsTool() {
  const { profileId, profile } = useSession();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const docs = useAsync(
    () => fetchCompanyDocuments(showRetired),
    [showRetired],
  );
  const rows = docs.data ?? [];

  // The bucket is private, so nothing here can point straight at storage.
  // Re-signed whenever the set of documents changes.
  const urls = useAsync(
    () => signCompanyDocuments(rows.map((row) => row.path)),
    [rows.map((row) => row.path).join(",")],
  );

  const mayDelete = profile?.can_delete_company_docs === true;
  // Read once per render rather than inside each row, so every expiry on the
  // screen is judged against the same day.
  const today = new Date().toISOString().slice(0, 10);

  const guard = async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
      await docs.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const add = async (files: FileList) => {
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        // The filename minus its extension is a far better first guess at a
        // name than "New document", and it is right often enough to leave alone.
        const name = file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ");
        await uploadCompanyDocument(file, { name }, profileId);
      }
      await docs.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (doc: CompanyDocument) => {
    if (
      !window.confirm(
        t(
          "Delete “{name}” for good? Retiring it instead keeps it on everything it has already gone out with.",
          { name: doc.name },
        ),
      )
    ) {
      return;
    }
    await guard(() => deleteCompanyDocument(doc));
  };

  return (
    <>
      <ErrorNote error={error} />

      <div className="uploadrow">
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,.pdf,image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) void add(files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="btn-ghost"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          {busy ? t("Uploading…") : t("Upload document")}
        </button>
        <span className="na-hint">
          {rows.length === 0
            ? t(
                "Licence, insurance, W-9, warranties — anything sent over and over.",
              )
            : tn(
                rows.length,
                "1 document. Pick from these on an estimate.",
                "{n} documents. Pick from these on an estimate.",
              )}
        </span>
        <label className="na-hint" style={{ marginLeft: "auto" }}>
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => setShowRetired(e.target.checked)}
          />{" "}
          {t("Show retired")}
        </label>
      </div>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Name")}</th>
              <th>{t("Kind")}</th>
              <th>{t("Expires")}</th>
              <th>{t("File")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="tnone">
                  {t("Nothing here yet.")}
                </td>
              </tr>
            ) : (
              rows.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  today={today}
                  url={urls.data?.[doc.path]}
                  mayDelete={mayDelete}
                  onSave={(patch) =>
                    void guard(() =>
                      updateRow("company_documents", doc.id, patch),
                    )
                  }
                  onDelete={() => void remove(doc)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {!mayDelete && rows.length > 0 && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "Retiring a document takes it out of the picker and leaves it on everything already sent. Deleting one outright is Lysa, Sam and Jonathon only.",
          )}
        </div>
      )}
    </>
  );
}

/**
 * The expiry notice, in the app's language.
 *
 * Whether to say anything, and how urgently, is `companyDocumentExpiry` in
 * packages/core; the English here is the sentence it gives, word for word.
 */
function expiryText(expiry: ExpiryNotice, expiresAt: string): string {
  if (expiry.expired) {
    return t("Expired {date}", { date: formatExpiryDate(expiresAt) });
  }
  if (expiry.days === 0) return t("Expires today");
  return tn(expiry.days, "Expires in 1 day", "Expires in {n} days");
}

function DocumentRow({
  doc,
  today,
  url,
  mayDelete,
  onSave,
  onDelete,
}: {
  doc: CompanyDocument;
  today: string;
  url: string | undefined;
  mayDelete: boolean;
  onSave: (patch: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const expiry = companyDocumentExpiry(doc, today);

  return (
    <tr className={doc.active ? undefined : "retiredrow"}>
      <td>
        <SavingInput
          value={doc.name}
          onSave={(value) => onSave({ name: value || doc.name })}
        />
      </td>
      <td>
        <SavingInput
          value={doc.category ?? ""}
          placeholder={t("Licence, Insurance…")}
          onSave={(value) => onSave({ category: value || null })}
        />
      </td>
      <td>
        <input
          type="date"
          defaultValue={doc.expires_at ?? ""}
          onChange={(e) => onSave({ expires_at: e.target.value || null })}
        />
        {expiry && doc.expires_at && (
          <div className={expiry.expired ? "expirybad" : "expirysoon"}>
            {expiryText(expiry, doc.expires_at)}
          </div>
        )}
      </td>
      <td>
        {url ? (
          <a href={url} target="_blank" rel="noreferrer">
            {doc.filename ?? t("Open")}
          </a>
        ) : (
          <span className="na-hint">{doc.filename ?? "—"}</span>
        )}
      </td>
      <td>
        <button
          type="button"
          className="btn-ghost"
          title={
            doc.active
              ? t(
                  "Take it out of the picker, leave it on what was already sent",
                )
              : t("Put it back in the picker")
          }
          onClick={() => onSave({ active: !doc.active })}
        >
          {doc.active ? t("Retire") : t("Restore")}
        </button>
        {mayDelete && (
          <button
            type="button"
            className="xbtn"
            title={t("Delete it and its file for good")}
            onClick={onDelete}
          >
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

/** Save on blur, Enter blurs — the same input every other Tools screen uses. */
function SavingInput({
  value,
  placeholder,
  onSave,
}: {
  value: string;
  placeholder?: string;
  onSave: (value: string) => void;
}) {
  const [local, setLocal] = useState(value);
  const [dirty, setDirty] = useState(false);
  if (!dirty && local !== value) setLocal(value);

  return (
    <input
      value={local}
      placeholder={placeholder}
      onChange={(e) => {
        setLocal(e.target.value);
        setDirty(true);
      }}
      onBlur={() => {
        if (!dirty) return;
        setDirty(false);
        onSave(local);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
